"use client";

import { TableEventBookingSheet } from "./table-event-booking-sheet";

import { CalendarDays, Ticket, Users } from "lucide-react";
import Link from "next/link";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { createClientId } from "@/lib/client-id";
import { questionAnswersError } from "@/lib/experience-questions";
import { dateInTimeZone } from "@/lib/zoned-date";
import type { PublicEvent, PublicMenu, PublicVenue } from "./domain";
import { MenuSelectionEditor } from "./menu-selection-editor";
import {
  type PublicMenuSelectionDraft,
  validatePublicMenuSelections,
} from "./menu-selection-state";
import {
  StripePaymentStep,
  type ReservationPaymentSession,
} from "./stripe-payment-step";

type EventHold = {
  publicId: string;
  holdToken: string;
  expiresAt: string;
  quantity: number;
  subtotalCents: number;
  discountCents: number;
  feeCents: number;
  taxCents: number;
  serviceChargeCents: number;
  gratuityCents: number;
  gratuityBasisPoints: number | null;
  totalCents: number;
  currency: string;
  lineItems: Array<{
    ticketProductId: string;
    name: string;
    quantity: number;
    unitPriceCents: number;
    subtotalCents: number;
    discountCents: number;
    taxCents: number;
  }>;
  feeItems: Array<{ id: string; name: string; totalCents: number; taxCents: number }>;
  discount: null | { id: string; name: string; code: string; amountCents: number };
  clubDiscounts?: Array<{ benefitId: string; title: string; amountCents: number }>;
};

type EventOrder = {
  confirmationCode: string;
  quantity: number;
  totalCents: number;
  tickets: Array<{ id: string; ticketNumber: number; credential: string }>;
  managementToken: string;
};

export function EventCheckoutSheet({
  event,
  ...props
}: {
  venue: PublicVenue;
  event: PublicEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!event) return null;
  if (event.bookingMode === "table_reservations") return <TableEventBookingSheet event={event} {...props} />;
  return <EventCheckoutSheetContent key={event.id} event={event} {...props} />;
}

function EventCheckoutSheetContent({
  venue,
  event,
  open,
  onOpenChange,
}: {
  venue: PublicVenue;
  event: PublicEvent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const ticketProducts = publicEventTicketProducts(event);
  const today = dateInTimeZone(new Date(), venue.timezone ?? "UTC");
  const eligibleOccurrences = event.occurrences.filter(
    (item) => item.date >= today && item.remaining > 0 && (item.salesStatus ?? "open") === "open",
  );
  const [step, setStep] = useState<"details" | "payment" | "confirmed">("details");
  const [occurrenceId, setOccurrenceId] = useState(
    eligibleOccurrences[0]?.id
      ?? "",
  );
  const [quantities, setQuantities] = useState<Record<string, number>>(
    ticketProducts[0] ? { [ticketProducts[0].id]: 1 } : {},
  );
  const [discountCode, setDiscountCode] = useState("");
  const [gratuityBasisPoints, setGratuityBasisPoints] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [textReminder, setTextReminder] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [menu, setMenu] = useState(event.menu);
  const [menuSelections, setMenuSelections] = useState<
    PublicMenuSelectionDraft[]
  >([]);
  const [hold, setHold] = useState<EventHold | null>(null);
  const [payment, setPayment] = useState<ReservationPaymentSession | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [order, setOrder] = useState<EventOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const orderKey = useRef(createClientId());
  const occurrence = eligibleOccurrences.find((item) => item.id === occurrenceId);
  const maxQuantity = Math.max(
    1,
    Math.min(
      occurrence?.remaining ?? 1,
      event.ticket.perPersonLimit ?? event.ticket.quantity,
    ),
  );
  const selections = ticketProducts
    .map((product) => ({
      ticketProductId: product.id,
      quantity: quantities[product.id] ?? 0,
    }))
    .filter((item) => item.quantity > 0);
  const totalQuantity = selections.reduce((total, item) => total + item.quantity, 0);
  const preview = previewEventPrice(event, selections, gratuityBasisPoints);
  const questions = event.questions ?? [];
  const postBookingQuestions = event.questionnaire?.timing === "after_booking";
  const checkoutQuestions = postBookingQuestions ? [] : questions;
  const questionError = questionAnswersError(checkoutQuestions, answers);
  const requiredQuestionsComplete = !questionError;
  const orderableMenuItemIds = new Set(
    menu?.sections.flatMap((section) =>
      section.items
        .filter((item) => item.orderable !== false)
        .map((item) => item.id),
    ) ?? [],
  );
  const effectiveMenuSelections = menuSelections.filter(
    (selection) =>
      selection.guestNumber <= totalQuantity &&
      orderableMenuItemIds.has(selection.itemId),
  );
  const menuSelectionPayload = effectiveMenuSelections.map(
    ({ guestNumber, ...selection }) => ({
      ticketNumber: guestNumber,
      ...selection,
    }),
  );
  const menuSelectionsValid = validatePublicMenuSelections(
    menu,
    effectiveMenuSelections,
    totalQuantity,
  );

  const releaseHold = async (activeHold: EventHold) => {
    await fetch(`/api/tables/event-holds/${activeHold.publicId}`, {
      method: "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        tenantSlug: venue.tenantSlug,
        holdToken: activeHold.holdToken,
      }),
      keepalive: true,
    });
  };

  const changeOpen = (nextOpen: boolean) => {
    if (!nextOpen && paymentCompleted && !order) {
      setError("Your payment completed. Keep this window open while Trellis Tables confirms the ticket order.");
      return;
    }
    if (!nextOpen && hold && !order) void releaseHold(hold);
    onOpenChange(nextOpen);
  };

  const restartCheckout = async () => {
    if (hold) await releaseHold(hold);
    setHold(null);
    setPayment(null);
    setPaymentCompleted(false);
    orderKey.current = createClientId();
    setStep("details");
  };

  const confirmOrder = async (activeHold = hold, activePayment = payment) => {
    if (!activeHold) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/tables/event-ticket-orders", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": orderKey.current,
        },
        body: JSON.stringify({
          tenantSlug: venue.tenantSlug,
          holdPublicId: activeHold.publicId,
          holdToken: activeHold.holdToken,
          payment: activePayment
            ? {
                sessionPublicId: activePayment.publicId,
                sessionToken: activePayment.sessionToken,
              }
            : undefined,
          guest: { firstName, lastName, email, phone: phone || undefined },
          answers: checkoutQuestions.flatMap((question) => [
            { questionId: question.id, answer: answers[question.id] ?? "" },
            ...(question.additionalTextPrompt
              ? [{ questionId: `${question.id}__note`, answer: answers[`${question.id}__note`] ?? "" }]
              : []),
          ]),
          consents: {
            termsAccepted: true,
            emailReminder: true,
            smsTransactional: textReminder,
            smsReminder: textReminder,
          },
        }),
      });
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.message ?? payload.detail ?? "The ticket order could not be confirmed.");
      setOrder(payload as EventOrder);
      setStep("confirmed");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The ticket order could not be confirmed.");
      throw cause;
    } finally {
      setBusy(false);
    }
  };

  const startCheckout = async () => {
    if (
      !occurrence ||
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !requiredQuestionsComplete
    ) return;
    setBusy(true);
    setError("");
    try {
      const holdResponse = await fetch(`/api/tables/events/${event.id}/holds`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": createClientId(),
        },
        body: JSON.stringify({
          tenantSlug: venue.tenantSlug,
          locationSlug: venue.locationSlug,
          occurrenceId,
          items: selections,
          discountCode: discountCode.trim() || undefined,
          gratuityBasisPoints,
          guestEmail: email.trim(),
          menuSelections: menuSelectionPayload,
          accessToken: venue.accessToken,
        }),
      });
      const holdPayload = await holdResponse.json();
      if (!holdResponse.ok) {
        if ("menu" in holdPayload) {
          setMenu((holdPayload.menu as PublicMenu | null) ?? undefined);
        }
        throw new Error(holdPayload.message ?? holdPayload.detail ?? "Those tickets are no longer available.");
      }
      const nextHold = holdPayload as EventHold;
      setHold(nextHold);
      if (nextHold.totalCents === 0) {
        await confirmOrder(nextHold, null);
        return;
      }
      const paymentResponse = await fetch(
        `/api/tables/event-holds/${nextHold.publicId}/payment-sessions`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "idempotency-key": createClientId(),
          },
          body: JSON.stringify({
            tenantSlug: venue.tenantSlug,
            holdToken: nextHold.holdToken,
          }),
        },
      );
      const paymentPayload = await paymentResponse.json();
      if (!paymentResponse.ok)
        throw new Error(paymentPayload.message ?? paymentPayload.detail ?? "Trellis Payments checkout could not be started.");
      const nextPayment = paymentPayload as ReservationPaymentSession;
      setPayment(nextPayment);
      setStep("payment");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Ticket checkout could not be started.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={changeOpen}>
      <SheetContent className="booking-sheet event-checkout-sheet">
        <SheetTitle className="sr-only">Tickets for {event.name}</SheetTitle>
        {step === "details" ? (
          <div className="booking-body booking-details">
            <p className="step-label">Event tickets</p>
            <h2>{event.name}</h2>
            <p>{event.description}</p>
            <label>
              <CalendarDays />
              <span>
                Date and time
                <select value={occurrenceId} onChange={(item) => setOccurrenceId(item.target.value)}>
                  {eligibleOccurrences.map((item) => (
                    <option key={item.id} value={item.id} disabled={item.remaining < 1 || (item.salesStatus ?? "open") !== "open"}>
                      {formatOccurrence(item)} · {eventOccurrenceAvailability(item)}
                    </option>
                  ))}
                </select>
              </span>
            </label>
            <div className="event-ticket-picker">
              <header><Users /><span><strong>Tickets</strong><small>{totalQuantity} of {maxQuantity} selected</small></span></header>
              {ticketProducts.map((product) => {
                const selected = quantities[product.id] ?? 0;
                const productMax = Math.min(
                  product.perPersonLimit ?? maxQuantity,
                  maxQuantity - totalQuantity + selected,
                );
                return (
                  <label key={product.id}>
                    <span><strong>{product.name}</strong>{product.description ? <small>{product.description}</small> : null}<em>{formatMoney(product.costPerTicketCents)}</em></span>
                    <select
                      aria-label={`${product.name} quantity`}
                      value={selected}
                      onChange={(item) =>
                        setQuantities({ ...quantities, [product.id]: Number(item.target.value) })
                      }
                    >
                      {Array.from({ length: productMax + 1 }, (_, index) => index).map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
            {menu ? (
              <MenuSelectionEditor
                menu={menu}
                guestCount={totalQuantity}
                value={effectiveMenuSelections}
                onChange={setMenuSelections}
                guestNoun="Ticket"
                description="Complete required choices for each ticket. Required modifiers apply once an item is selected."
              />
            ) : null}
            <div className="booking-contact-grid">
              <label>First name<Input value={firstName} onChange={(item) => setFirstName(item.target.value)} /></label>
              <label>Last name<Input value={lastName} onChange={(item) => setLastName(item.target.value)} /></label>
              <label>Email<Input type="email" value={email} onChange={(item) => setEmail(item.target.value)} /></label>
              <label>Phone<Input type="tel" value={phone} onChange={(item) => setPhone(item.target.value)} /></label>
            </div>
            {checkoutQuestions.length ? (
              <div className="event-booking-questions">
                <header>
                  <strong>A few details for the restaurant</strong>
                  <small>Your answers travel with this ticket order.</small>
                </header>
                {checkoutQuestions.map((question) => (
                  <label key={question.id}>
                    {question.shortCode?.trim() || question.label}
                    {question.required ? <em>Required</em> : <small>Optional</small>}
                    {question.type === "single_select" ? (
                      <select
                        value={answers[question.id] ?? ""}
                        onChange={(change) => setAnswers((current) => ({ ...current, [question.id]: change.target.value }))}
                      >
                        <option value="">Choose an answer</option>
                        {question.options?.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                      </select>
                    ) : question.type === "multi_select" ? (
                      <span className="event-question-options">
                        {question.options?.map((option) => {
                          const selected = parseMultiAnswer(answers[question.id]).includes(option.id);
                          return (
                            <span key={option.id}>
                              <Checkbox
                                checked={selected}
                                onCheckedChange={(checked) => setAnswers((current) => ({
                                  ...current,
                                  [question.id]: JSON.stringify(
                                    checked
                                      ? [...new Set([...parseMultiAnswer(current[question.id]), option.id])]
                                      : parseMultiAnswer(current[question.id]).filter((id) => id !== option.id),
                                  ),
                                }))}
                              />
                              {option.label}
                            </span>
                          );
                        })}
                      </span>
                    ) : question.type === "long_text" ? (
                      <Textarea
                        maxLength={5000}
                        value={answers[question.id] ?? ""}
                        onChange={(change) => setAnswers((current) => ({ ...current, [question.id]: change.target.value }))}
                      />
                    ) : (
                      <Input
                        maxLength={500}
                        value={answers[question.id] ?? ""}
                        onChange={(change) => setAnswers((current) => ({ ...current, [question.id]: change.target.value }))}
                      />
                    )}
                    {question.additionalTextPrompt && (question.type === "single_select" || question.type === "multi_select") ? (
                      <span>
                        {question.additionalTextPrompt}
                        <Textarea
                          maxLength={5000}
                          value={answers[`${question.id}__note`] ?? ""}
                          onChange={(change) => setAnswers((current) => ({ ...current, [`${question.id}__note`]: change.target.value }))}
                        />
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
            ) : null}
            {postBookingQuestions && questions.length ? <p className="event-question-after-booking-note">You can answer these questions from your secure ticket link after booking.</p> : null}
            <label className="booking-consent-row">
              <Checkbox
                checked={textReminder}
                disabled={!phone.trim()}
                onCheckedChange={(checked) => setTextReminder(Boolean(checked))}
              />
              <span>Text me a transactional reminder before this event.</span>
            </label>
            <label className="event-discount-code">
              Discount code
              <Input value={discountCode} onChange={(item) => setDiscountCode(item.target.value.toUpperCase())} placeholder="Optional" />
              {discountCode.trim() ? <small>The code and its usage limit are checked when your tickets are held.</small> : null}
              <small>Signed-in club members receive eligible Trellis perks when tickets are held. Your Tables account must be linked to your membership.</small>
            </label>
            {event.ticket.serviceMode === "optional" ? (
              <section className="event-gratuity-selector" aria-labelledby="event-gratuity-title">
                <header>
                  <span>
                    <strong id="event-gratuity-title">Optional gratuity</strong>
                    <small>Choose an amount for the service team. No gratuity is selected by default.</small>
                  </span>
                  <strong>{formatMoney(preview.gratuityCents)}</strong>
                </header>
                <div>
                  {gratuityOptions(event.ticket.serviceChargeBasisPoints).map((basisPoints) => (
                    <button
                      aria-pressed={gratuityBasisPoints === basisPoints}
                      key={basisPoints}
                      onClick={() => setGratuityBasisPoints(basisPoints)}
                      type="button"
                    >
                      {basisPoints === 0 ? "No tip" : `${basisPoints / 100}%`}
                    </button>
                  ))}
                </div>
                <label>
                  Custom percentage
                  <Input
                    inputMode="decimal"
                    max={100}
                    min={0}
                    step="0.01"
                    type="number"
                    value={gratuityBasisPoints / 100}
                    onChange={(change) => {
                      const percentage = Math.min(100, Math.max(0, Number(change.target.value) || 0));
                      setGratuityBasisPoints(Math.round(percentage * 100));
                    }}
                  />
                </label>
                <small>Calculated on ticket prices after discounts and frozen before secure checkout.</small>
              </section>
            ) : null}
            <EventReceipt price={preview} pendingDiscount={Boolean(discountCode.trim())} />
            {questionError ? <p className="booking-error" role="alert">{questionError}</p> : null}
            {error ? <p className="booking-error" role="alert">{error}</p> : null}
            <Button disabled={busy || !occurrence || totalQuantity < 1 || !firstName.trim() || !lastName.trim() || !email.trim() || !requiredQuestionsComplete || !menuSelectionsValid} onClick={() => void startCheckout()}>
              {busy ? "Holding tickets…" : preview.totalCents || discountCode.trim() ? "Review and continue" : "Confirm tickets"}
            </Button>
          </div>
        ) : null}
        {step === "payment" && payment && hold ? (
          <div className="event-payment-review">
            <EventReceipt price={hold} />
            <StripePaymentStep
              session={payment}
              verifyEndpoint={`/api/tables/event-payment-sessions/${payment.publicId}/verify`}
              onBack={() => void restartCheckout()}
              onComplete={async () => {
                setPaymentCompleted(true);
                await confirmOrder();
              }}
            />
          </div>
        ) : null}
        {step === "confirmed" && order ? (
          <div className="booking-body booking-confirmation">
            <Ticket />
            <p className="step-label">Tickets confirmed</p>
            <h2>You&apos;re on the guest list.</h2>
            <p>{event.name} · confirmation {order.confirmationCode}</p>
            {postBookingQuestions && questions.length ? <p>We&apos;ll ask a few questions on your secure ticket page before the event.</p> : null}
            <div className="event-ticket-list">
              {order.tickets.map((item) => (
                <div key={item.id}><strong>Ticket {item.ticketNumber}</strong><small>Mobile QR ready</small></div>
              ))}
            </div>
            <Button asChild><Link href={`/event-manage/${order.managementToken}`}>Open mobile tickets</Link></Button>
            <Button variant="outline" disabled={busy} onClick={() => changeOpen(false)}>Done</Button>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

type DisplayPrice = Pick<
  EventHold,
  | "subtotalCents"
  | "discountCents"
  | "feeCents"
  | "taxCents"
  | "serviceChargeCents"
  | "gratuityCents"
  | "gratuityBasisPoints"
  | "totalCents"
  | "lineItems"
  | "feeItems"
  | "discount"
  | "clubDiscounts"
>;

function EventReceipt({
  price,
  pendingDiscount = false,
}: {
  price: DisplayPrice;
  pendingDiscount?: boolean;
}) {
  return (
    <div className="event-price-receipt">
      <header><Ticket /><strong>Order summary</strong></header>
      {price.lineItems.map((line) => (
        <div key={line.ticketProductId}>
          <span>{line.quantity} × {line.name}</span>
          <strong>{formatMoney(line.subtotalCents)}</strong>
        </div>
      ))}
      {price.clubDiscounts?.map(perk => <div className="event-price-discount" key={perk.benefitId}><span>{perk.title} · Club perk</span><strong>−{formatMoney(perk.amountCents)}</strong></div>)}
      {price.discount ? <div className="event-price-discount"><span>{price.discount.name}</span><strong>−{formatMoney(price.discount.amountCents)}</strong></div> : null}
      {price.discountCents > 0 && !price.discount && !price.clubDiscounts?.length ? <div className="event-price-discount"><span>Discount</span><strong>−{formatMoney(price.discountCents)}</strong></div> : null}
      {pendingDiscount && price.discountCents === 0 ? <div><span>Discount code</span><strong>Pending</strong></div> : null}
      {price.feeItems.map((fee) => <div key={fee.id}><span>{fee.name}</span><strong>{formatMoney(fee.totalCents)}</strong></div>)}
      {price.serviceChargeCents > 0 ? <div><span>Service charge</span><strong>{formatMoney(price.serviceChargeCents)}</strong></div> : null}
      {price.gratuityCents > 0 ? <div><span>Optional gratuity{price.gratuityBasisPoints ? ` (${price.gratuityBasisPoints / 100}%)` : ""}</span><strong>{formatMoney(price.gratuityCents)}</strong></div> : null}
      {price.taxCents > 0 ? <div><span>Tax</span><strong>{formatMoney(price.taxCents)}</strong></div> : null}
      <footer><span>Total</span><strong>{formatMoney(price.totalCents)}</strong></footer>
    </div>
  );
}

const previewEventPrice = (
  event: PublicEvent,
  selection: Array<{ ticketProductId: string; quantity: number }>,
  gratuityBasisPoints: number,
): DisplayPrice => {
  const products = new Map(publicEventTicketProducts(event).map((product) => [product.id, product]));
  const lineItems = selection.flatMap((item) => {
    const product = products.get(item.ticketProductId);
    if (!product) return [];
    const subtotalCents = product.costPerTicketCents * item.quantity;
    const taxCents = product.taxCode === "non_taxable"
      ? 0
      : Math.round((subtotalCents * product.taxRateBasisPoints) / 10000);
    return [{
      ticketProductId: product.id,
      name: product.name,
      quantity: item.quantity,
      unitPriceCents: product.costPerTicketCents,
      subtotalCents,
      discountCents: 0,
      taxCents,
    }];
  });
  const feeItems = (event.fees ?? []).map((fee) => ({
    id: fee.id,
    name: fee.name,
    totalCents: fee.priceCents,
    taxCents: Math.round((fee.priceCents * (fee.taxRateBasisPoints ?? 0)) / 10000),
  }));
  const subtotalCents = lineItems.reduce((total, line) => total + line.subtotalCents, 0);
  const feeCents = feeItems.reduce((total, fee) => total + fee.totalCents, 0);
  const taxCents = lineItems.reduce((total, line) => total + line.taxCents, 0)
    + feeItems.reduce((total, fee) => total + fee.taxCents, 0);
  const serviceChargeCents = event.ticket.serviceMode === "required"
    ? Math.round((subtotalCents * (event.ticket.serviceChargeBasisPoints ?? 0)) / 10000)
    : 0;
  const gratuityCents = event.ticket.serviceMode === "optional"
    ? Math.round((subtotalCents * gratuityBasisPoints) / 10000)
    : 0;
  return {
    lineItems,
    feeItems,
    discount: null,
    subtotalCents,
    discountCents: 0,
    feeCents,
    taxCents,
    serviceChargeCents,
    gratuityCents,
    gratuityBasisPoints: event.ticket.serviceMode === "optional" ? gratuityBasisPoints : null,
    totalCents: subtotalCents + feeCents + taxCents + serviceChargeCents + gratuityCents,
  };
};

const gratuityOptions = (suggested: number | null) =>
  [...new Set([0, suggested ?? 0, 1800, 2000, 2500])].sort((left, right) => left - right);

const publicEventTicketProducts = (event: PublicEvent) =>
  event.ticketProducts?.length
    ? event.ticketProducts
    : [
        {
          id: "general-admission",
          name: "General admission",
          costPerTicketCents: event.ticket.costPerTicketCents,
          perPersonLimit: event.ticket.perPersonLimit,
          taxCode: "non_taxable" as const,
          taxRateBasisPoints: 0,
          mobileTickets: event.ticket.mobileTickets,
        },
      ];

const parseMultiAnswer = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) && parsed.every((item) => typeof item === "string")
      ? parsed
      : [];
  } catch {
    return [];
  }
};

const formatMoney = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

const formatOccurrence = (value: PublicEvent["occurrences"][number]) =>
  `${new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(`${value.date}T12:00:00`))} at ${new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(new Date(`${value.date}T${value.startTime}:00`))}`;

const eventOccurrenceAvailability = (value: PublicEvent["occurrences"][number]) => {
  if (value.salesStatus === "scheduled")
    return value.salesOpenAt
      ? `sales open ${new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value.salesOpenAt))}`
      : "sales open later";
  if (value.salesStatus === "closed") return "sales closed";
  return `${value.remaining} left`;
};
